import { Alert, Breadcrumb, Button, Card, DatePicker, Form, Grid, Select, Skeleton, Space, Table, TimePicker, Typography, App as AntApp } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { classKeys, formatDate } from "../app/classes.ts";
import {
  cellKey,
  defaultPeriodCount,
  defaultTimes,
  maxPeriod,
  shortTime,
  timetableKeys,
  weekDays,
  type DayOfWeek,
  type PeriodTimes,
  type TimetableDetails,
  type TimetableSlotInput,
} from "../app/timetable.ts";
import { lessonKeys } from "../app/lessons.ts";
import { ClassLessonsCard } from "../components/ClassLessonsCard.tsx";
import { GenerateLessonsCard } from "../components/GenerateLessonsCard.tsx";

/** Wide enough for a full "HH:mm"; the clock icon is hidden so the text gets the room. */
const TIME_INPUT_WIDTH = 72;
const PERIOD_COLUMN_WIDTH = 2 * TIME_INPUT_WIDTH + 26;
const DAY_COLUMN_WIDTH = 160;

/** Field name matches the API (validFrom). */
interface SaveForm {
  validFrom?: Dayjs;
}

function timeValue(time: string): Dayjs {
  const [h = "0", m = "0"] = time.split(":");
  return dayjs().hour(Number(h)).minute(Number(m)).second(0);
}

/** Today when it lies inside the academic year, otherwise the year start. */
function defaultValidFrom(data: TimetableDetails): Dayjs {
  const today = dayjs();
  return today.isBefore(dayjs(data.academicYearStart)) || today.isAfter(dayjs(data.academicYearEnd)) ? dayjs(data.academicYearStart) : today;
}

/**
 * Weekly timetable editor of a class: weekdays (without the year's day off) as columns, periods as rows with their times,
 * a subject of the class's course in each cell. Saving replaces the whole grid from the chosen date; older versions stay
 * viewable. Below, lessons are generated from the grid after a preview count.
 */
export function ClassTimetablePage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("academic.manage");
  const [form] = Form.useForm<SaveForm>();
  const [viewDate, setViewDate] = useState<string>();
  const [cells, setCells] = useState<Record<string, string>>({});
  const [times, setTimes] = useState<PeriodTimes[]>([]);
  const [summary, setSummary] = useState<string[]>([]);

  const details = useQuery({
    queryKey: classKeys.detail(id),
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes/{id}", { params: { path: { id } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
  const timetable = useQuery({
    queryKey: timetableKeys.at(id, viewDate),
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes/{id}/timetable", { params: { path: { id }, query: viewDate ? { date: viewDate } : {} } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  // Each loaded version fills the editor: its cells, and one time row per period (the first slot's times, or defaults).
  useEffect(() => {
    const data = timetable.data;
    if (!data) return;
    const periods = Math.max(defaultPeriodCount, ...data.slots.map((s) => Number(s.period)));
    setCells(Object.fromEntries(data.slots.map((s) => [cellKey(s.weekday, Number(s.period)), s.subjectId])));
    setTimes(
      Array.from({ length: periods }, (_, i) => {
        const slot = data.slots.find((s) => Number(s.period) === i + 1);
        return slot ? { startTime: shortTime(slot.startTime), endTime: shortTime(slot.endTime) } : defaultTimes(i + 1);
      }),
    );
    form.setFieldsValue({ validFrom: defaultValidFrom(data) });
    setSummary([]);
  }, [timetable.data, form]);

  const days = weekDays.filter((d) => d !== timetable.data?.weeklyDayOff);

  const save = useMutation({
    mutationFn: async (values: SaveForm) => {
      const slots: TimetableSlotInput[] = [];
      const labels: string[] = [];
      times.forEach((time, i) => {
        for (const weekday of days) {
          const subjectId = cells[cellKey(weekday, i + 1)];
          if (!subjectId) continue;
          slots.push({ weekday, period: i + 1, startTime: `${time.startTime}:00`, endTime: `${time.endTime}:00`, subjectId });
          labels.push(t("timetable.cellLabel", { day: t(`academicYears.days.${weekday}`), period: i + 1 }));
        }
      });
      const validFrom = values.validFrom?.format("YYYY-MM-DD") ?? null;
      const { data, error, response } = await api.PUT("/academic/classes/{id}/timetable", { params: { path: { id } }, body: { validFrom, slots } });
      if (!data) throw Object.assign(new ApiError(response.status, error), { labels });
      return data;
    },
    onSuccess: async (result) => {
      void message.success(t("timetable.saved", { removed: Number(result.lessonsRemoved), created: Number(result.lessonsCreated) }));
      if (Number(result.lessonsKeptWithRecords) > 0) void message.info(t("timetable.keptWithRecords", { count: Number(result.lessonsKeptWithRecords) }));
      queryClient.setQueryData(timetableKeys.at(id, result.timetable.date), result.timetable);
      setViewDate(result.timetable.date);
      await queryClient.invalidateQueries({ queryKey: timetableKeys.all(id) });
      await queryClient.invalidateQueries({ queryKey: lessonKeys.all(id) });
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (err) => setSummary(saveErrors(err)),
  });

  /** validFrom errors go under the date input; slot errors are listed with their weekday and period. */
  function saveErrors(err: unknown): string[] {
    if (!(err instanceof ApiError)) return [t("app.error")];
    const labels = (err as ApiError & { labels?: string[] }).labels ?? [];
    const lines: string[] = [];
    for (const [path, messages] of Object.entries(err.fieldErrors())) {
      const slot = /^slots\[(\d+)\]/.exec(path);
      if (path === "validFrom") form.setFields([{ name: "validFrom", errors: messages }]);
      else if (slot) lines.push(...messages.map((m) => `${labels[Number(slot[1])] ?? ""}: ${m}`));
      else lines.push(...messages);
    }
    return lines.length === 0 && Object.keys(err.fieldErrors()).length === 0 ? err.messages(t("app.error")) : lines;
  }

  if (details.isPending || timetable.isPending) return <Skeleton active />;
  if (details.isError || timetable.isError || !details.data || !timetable.data) return <Alert type="error" showIcon title={t("app.error")} />;
  const schoolClass = details.data;
  const grid = timetable.data;
  const subjectOptions = schoolClass.subjects.map((s) => ({ value: s.subjectId, label: s.subjectName }));

  const setTime = (index: number, field: keyof PeriodTimes, value: Dayjs | null) => {
    if (!value) return;
    setTimes((current) => current.map((row, i) => (i === index ? { ...row, [field]: value.format("HH:mm") } : row)));
  };

  const setCell = (key: string, subjectId: string | undefined) =>
    setCells((current) => {
      const next = { ...current };
      if (subjectId) next[key] = subjectId;
      else delete next[key];
      return next;
    });

  const removeLastPeriod = () => {
    const period = times.length;
    setCells((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !key.endsWith(`-${period}`))));
    setTimes((current) => current.slice(0, -1));
  };

  const columns = [
    {
      title: t("timetable.period"),
      key: "period",
      width: PERIOD_COLUMN_WIDTH,
      fixed: "left" as const,
      render: (_: unknown, row: { period: number }) => {
        const time = times[row.period - 1] ?? defaultTimes(row.period);
        return (
          <Space orientation="vertical" size={4}>
            <Typography.Text strong>{t("timetable.periodNumber", { period: row.period })}</Typography.Text>
            <Space.Compact size="small">
              <TimePicker aria-label={t("timetable.startTimeLabel", { period: row.period })} format="HH:mm" minuteStep={5} allowClear={false} needConfirm={false} suffixIcon={null} disabled={!canManage} value={timeValue(time.startTime)} onChange={(v) => setTime(row.period - 1, "startTime", v)} style={{ width: TIME_INPUT_WIDTH }} />
              <TimePicker aria-label={t("timetable.endTimeLabel", { period: row.period })} format="HH:mm" minuteStep={5} allowClear={false} needConfirm={false} suffixIcon={null} disabled={!canManage} value={timeValue(time.endTime)} onChange={(v) => setTime(row.period - 1, "endTime", v)} style={{ width: TIME_INPUT_WIDTH }} />
            </Space.Compact>
          </Space>
        );
      },
    },
    ...days.map((weekday: DayOfWeek) => ({
      title: t(`academicYears.days.${weekday}`),
      key: weekday,
      width: DAY_COLUMN_WIDTH,
      render: (_: unknown, row: { period: number }) => (
        <Select
          aria-label={t("timetable.cellLabel", { day: t(`academicYears.days.${weekday}`), period: row.period })}
          style={{ width: "100%" }}
          allowClear
          disabled={!canManage}
          placeholder={t("timetable.noLesson")}
          options={subjectOptions}
          value={cells[cellKey(weekday, row.period)]}
          onChange={(value: string | undefined) => setCell(cellKey(weekday, row.period), value)}
        />
      ),
    })),
  ];

  const versionOptions = [...grid.versions].reverse().map((v) => ({ value: v, label: t("timetable.versionFrom", { date: formatDate(v) }) }));
  const versionSelect = versionOptions.length > 0 && (
    <Select aria-label={t("timetable.version")} style={{ minWidth: 200 }} options={versionOptions} value={grid.validFrom} placeholder={t("timetable.version")} onChange={(v: string) => setViewDate(v)} />
  );
  // On a phone the card header has no room beside the title, so the version picker moves into the body.
  const versionInHeader = screens.sm === true;

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Breadcrumb
        items={[
          { title: <Link to="/academic/classes">{t("classes.title")}</Link> },
          { title: <Link to={`/academic/classes/${schoolClass.id}`}>{schoolClass.name}</Link> },
          { title: t("timetable.title") },
        ]}
      />
      <Card
        title={
          <Typography.Title level={3} style={{ margin: 0, whiteSpace: "normal" }}>
            {t("timetable.heading", { name: schoolClass.name })}
          </Typography.Title>
        }
        extra={versionInHeader && versionSelect}
      >
        <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
          {!versionInHeader && versionSelect}
          {grid.validFrom ? (
            <Typography.Text type="secondary">{t("timetable.showing", { date: formatDate(grid.date), from: formatDate(grid.validFrom) })}</Typography.Text>
          ) : (
            <Alert type="info" showIcon title={t("timetable.empty", { date: formatDate(grid.date) })} />
          )}
          {subjectOptions.length === 0 && <Alert type="warning" showIcon title={t("classes.noSubjects")} />}
          {summary.length > 0 && <Alert type="error" showIcon role="alert" title={summary.map((m) => <div key={m}>{m}</div>)} />}
          <Table<{ period: number }>
            rowKey="period"
            size="small"
            bordered
            columns={columns}
            dataSource={times.map((_, i) => ({ period: i + 1 }))}
            pagination={false}
            scroll={{ x: PERIOD_COLUMN_WIDTH + days.length * DAY_COLUMN_WIDTH }}
          />
          {canManage && (
            <Space wrap>
              <Button icon={<PlusOutlined />} disabled={times.length >= maxPeriod} onClick={() => setTimes((current) => [...current, defaultTimes(current.length + 1)])}>
                {t("timetable.addPeriod")}
              </Button>
              <Button icon={<MinusOutlined />} disabled={times.length <= 1} onClick={removeLastPeriod}>
                {t("timetable.removePeriod")}
              </Button>
            </Space>
          )}
          {canManage && (
            <Form<SaveForm> form={form} layout="inline" requiredMark={false} onFinish={(values) => save.mutate(values)}>
              <Form.Item name="validFrom" label={t("timetable.validFrom")} extra={t("timetable.validFromHint")} style={{ maxWidth: "100%" }} rules={[{ required: true, message: t("family.validation.required") }]}>
                <DatePicker format="DD.MM.YYYY" allowClear={false} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={save.isPending}>
                  {t("family.save")}
                </Button>
              </Form.Item>
            </Form>
          )}
        </Space>
      </Card>
      {canManage && <GenerateLessonsCard classId={schoolClass.id} yearStart={grid.academicYearStart} yearEnd={grid.academicYearEnd} />}
      <ClassLessonsCard classId={schoolClass.id} yearStart={grid.academicYearStart} yearEnd={grid.academicYearEnd} canManage={canManage} />
    </Space>
  );
}
